import React, { useMemo, useState } from 'react';
import { ArrowDown, ArrowLeft, ArrowUp, Camera, ChevronDown, Clapperboard, Plus, Sparkles, Trash2 } from 'lucide-react';
import GenerationOverlay from '../components/GenerationOverlay';
import { generateStoryboard } from '../api/storyboardApi';
import { cloneDemoShots, demoDuration } from '../demo/mockProject';

const shotSizes = ['极特写', '特写', '近景', '中景', '全景', '大全景'];
const cameraMoves = ['固定镜头', '缓慢推进', '后拉', '横向跟拍', '环绕运镜', '手持跟拍', '航拍俯冲'];

const fallbackShots = [
  { id: 's1', title: '全景', shotSize: '全景', cameraMove: '缓慢推进', lighting: '雨夜霓虹', emotion: '孤独', composition: '三分法构图', environment: '潮湿街道、霓虹灯', character: '男人', desc: 'A man in a trench coat walks alone through a rain-soaked neon street.', duration: 3 },
  { id: 's2', title: '中景', shotSize: '中景', cameraMove: '横向跟拍', lighting: '蓝紫侧光', emotion: '克制', composition: '人物位于画面左侧', environment: '烟雾、店铺橱窗', character: '男人', desc: 'He pauses beneath a flickering sign and lights a cigarette.', duration: 3 },
  { id: 's3', title: '特写', shotSize: '特写', cameraMove: '固定镜头', lighting: '高反差轮廓光', emotion: '若有所思', composition: '眼神特写', environment: '雨滴、烟雾', character: '男人', desc: 'Rain rolls across his face as he looks toward an unseen figure.', duration: 2 },
];

function Field({ label, children }) {
  return <label className="block"><span className="mb-1.5 block text-[11px] font-medium text-gray-500">{label}</span>{children}</label>;
}

function SelectField({ value, options, onChange }) {
  const visibleOptions = value && !options.includes(value) ? [value, ...options] : options;
  return (
    <div className="relative">
      <select value={value || ''} onChange={(event) => onChange(event.target.value)} className="w-full appearance-none rounded-lg border border-white/10 bg-[#111318] px-3 py-2 pr-8 text-xs text-gray-300 outline-none focus:border-blue-500/60">
        {visibleOptions.map((option) => <option key={option}>{option}</option>)}
      </select>
      <ChevronDown className="pointer-events-none absolute right-2.5 top-2.5 h-3.5 w-3.5 text-gray-600" />
    </div>
  );
}

function StoryboardResultPage({ onNavigate, pageData }) {
  const isDemo = Boolean(pageData?.demoMode);
  const [shots, setShots] = useState(() => pageData?.shots?.length ? pageData.shots.map((shot) => ({ ...shot })) : fallbackShots);
  const [activeId, setActiveId] = useState(shots[0]?.id);
  const [showOriginal, setShowOriginal] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationError, setGenerationError] = useState('');
  const activeShot = shots.find((shot) => shot.id === activeId) || shots[0];
  const totalDuration = useMemo(() => shots.reduce((sum, shot) => sum + Number(shot.duration || 0), 0), [shots]);

  const updateShot = (id, patch) => setShots((current) => current.map((shot) => shot.id === id ? { ...shot, ...patch } : shot));
  const moveShot = (index, direction) => {
    const nextIndex = index + direction;
    if (nextIndex < 0 || nextIndex >= shots.length) return;
    setShots((current) => {
      const copy = [...current];
      [copy[index], copy[nextIndex]] = [copy[nextIndex], copy[index]];
      return copy;
    });
  };
  const addShot = (index) => {
    const shot = { id: `new-${Date.now()}`, title: '中景', shotSize: '中景', cameraMove: '固定镜头', lighting: '电影感自然光', emotion: '根据语境', composition: '三分法构图', environment: '待补充', character: '待识别', desc: '', duration: 3 };
    setShots((current) => [...current.slice(0, index), shot, ...current.slice(index)]);
    setActiveId(shot.id);
  };
  const deleteShot = (id) => {
    if (shots.length === 1) return;
    const index = shots.findIndex((shot) => shot.id === id);
    const remaining = shots.filter((shot) => shot.id !== id);
    setShots(remaining);
    if (activeId === id) setActiveId(remaining[Math.min(index, remaining.length - 1)].id);
  };

  const handleGenerate = () => {
    if (!shots.some((shot) => shot.desc?.trim())) {
      setGenerationError('请至少为一个镜头填写画面描述。');
      return;
    }
    setGenerationError('');
    setIsGenerating(true);
  };

  const finishDemoGeneration = () => {
    const assets = cloneDemoShots();
    const generated = shots.map((shot, index) => ({
      ...shot,
      img: assets[index % assets.length].img,
      largeImg: assets[index % assets.length].largeImg,
      status: 'ready',
    }));
    setIsGenerating(false);
    onNavigate('frame-editor', { ...pageData, shots: generated, demoMode: true });
  };

  const finishRealGeneration = async () => {
    try {
      const result = await generateStoryboard(shots);
      if (!result?.ok || !result?.results) throw new Error(result?.error || '生成失败');
      const generated = shots.map((shot) => {
        const asset = result.results.find((item) => item.shotId === shot.id);
        return { ...shot, img: asset?.thumbUrl || asset?.imageUrl, largeImg: asset?.imageUrl, status: asset ? 'ready' : 'error' };
      });
      onNavigate('frame-editor', { ...pageData, shots: generated });
    } catch (error) {
      setGenerationError(error.message || '分镜生成失败，请稍后重试。');
      setIsGenerating(false);
    }
  };

  return (
    <div className="min-h-full bg-[#0d0f13] p-5 md:p-7">
      <div className="mx-auto max-w-[1500px]">
        <header className="mb-5 flex flex-col justify-between gap-4 xl:flex-row xl:items-center">
          <div className="flex items-center gap-4">
            <button onClick={() => onNavigate(pageData?.workflow === 'video' ? 'video' : 'storyboard')} className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-gray-400 hover:text-white"><ArrowLeft className="h-5 w-5" /></button>
            <div>
              <div className="flex items-center gap-2"><h1 className="text-xl font-semibold text-white">{pageData?.projectTitle || 'AI 分镜生成'}</h1>{isDemo && <span className="rounded-full bg-emerald-400/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider text-emerald-400">Demo Ready</span>}</div>
              <p className="mt-1 text-xs text-gray-500">已解析 {shots.length} 个镜头 · 预计 {totalDuration} 秒</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => setShowOriginal((value) => !value)} className="rounded-xl border border-white/10 px-4 py-2.5 text-sm text-gray-300 hover:bg-white/5">{showOriginal ? '返回分镜' : '查看原始剧本'}</button>
            <button onClick={handleGenerate} className="flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-500"><Sparkles className="h-4 w-4" /> 一键生成分镜图</button>
          </div>
        </header>

        {generationError && <div className="mb-4 rounded-xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-300">{generationError}</div>}

        {showOriginal ? (
          <section className="rounded-3xl border border-white/10 bg-[#15171c] p-7"><p className="mb-4 text-xs font-semibold uppercase tracking-[0.2em] text-gray-500">Original screenplay</p><pre className="whitespace-pre-wrap font-mono text-sm leading-7 text-gray-300">{pageData?.prompt || '暂无原始剧本'}</pre></section>
        ) : (
          <div className="grid gap-5 xl:grid-cols-[330px_minmax(0,1fr)]">
            <aside className="rounded-2xl border border-white/10 bg-[#15171c] p-3 xl:max-h-[calc(100vh-145px)] xl:overflow-y-auto">
              <div className="mb-3 flex items-center justify-between px-2 py-2"><span className="text-xs font-semibold uppercase tracking-[0.18em] text-gray-500">镜头列表</span><button onClick={() => addShot(shots.length)} className="flex items-center gap-1 text-xs text-blue-400"><Plus className="h-3.5 w-3.5" /> 添加镜头</button></div>
              <div className="space-y-2">
                {shots.map((shot, index) => (
                  <button key={shot.id} onClick={() => setActiveId(shot.id)} className={`group w-full rounded-xl border p-3 text-left transition ${activeShot?.id === shot.id ? 'border-blue-500/50 bg-blue-500/10' : 'border-transparent bg-white/[0.025] hover:bg-white/5'}`}>
                    <div className="flex items-start gap-3">
                      <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-xs font-semibold ${activeShot?.id === shot.id ? 'bg-blue-500 text-white' : 'bg-white/5 text-gray-500'}`}>{String(index + 1).padStart(2, '0')}</span>
                      <div className="min-w-0 flex-1"><div className="flex items-center justify-between"><span className="text-xs font-medium text-gray-200">{shot.shotSize || shot.title}</span><span className="text-[10px] text-gray-600">{shot.duration || 3}s</span></div><p className="mt-1 line-clamp-2 text-[11px] leading-4 text-gray-500">{shot.desc || '未填写画面描述'}</p></div>
                    </div>
                  </button>
                ))}
              </div>
            </aside>

            {activeShot && (
              <main className="rounded-2xl border border-white/10 bg-[#15171c] p-5 md:p-7">
                <div className="mb-6 flex items-start justify-between gap-4 border-b border-white/10 pb-5">
                  <div className="flex items-center gap-3"><span className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-500/10 text-blue-400"><Camera className="h-5 w-5" /></span><div><p className="text-xs text-gray-500">镜头 {shots.indexOf(activeShot) + 1} / {shots.length}</p><h2 className="mt-1 text-lg font-semibold text-white">镜头语言与画面设计</h2></div></div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => moveShot(shots.indexOf(activeShot), -1)} className="rounded-lg p-2 text-gray-500 hover:bg-white/5 hover:text-white" title="上移"><ArrowUp className="h-4 w-4" /></button>
                    <button onClick={() => moveShot(shots.indexOf(activeShot), 1)} className="rounded-lg p-2 text-gray-500 hover:bg-white/5 hover:text-white" title="下移"><ArrowDown className="h-4 w-4" /></button>
                    <button onClick={() => deleteShot(activeShot.id)} className="rounded-lg p-2 text-gray-500 hover:bg-red-500/10 hover:text-red-400" title="删除"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>

                <div className="grid gap-5 lg:grid-cols-[1fr_260px]">
                  <div className="space-y-5">
                    <Field label="画面细节描述"><textarea value={activeShot.desc || ''} onChange={(event) => updateShot(activeShot.id, { desc: event.target.value })} className="h-36 w-full resize-none rounded-xl border border-white/10 bg-[#111318] p-4 text-sm leading-6 text-gray-200 outline-none focus:border-blue-500/60" /></Field>
                    <div className="grid gap-4 sm:grid-cols-2">
                      <Field label="光影色调"><input value={activeShot.lighting || ''} onChange={(event) => updateShot(activeShot.id, { lighting: event.target.value })} className="w-full rounded-lg border border-white/10 bg-[#111318] px-3 py-2 text-xs text-gray-300 outline-none focus:border-blue-500/60" /></Field>
                      <Field label="人物神态 / 情绪"><input value={activeShot.emotion || ''} onChange={(event) => updateShot(activeShot.id, { emotion: event.target.value })} className="w-full rounded-lg border border-white/10 bg-[#111318] px-3 py-2 text-xs text-gray-300 outline-none focus:border-blue-500/60" /></Field>
                      <Field label="画面构图"><input value={activeShot.composition || ''} onChange={(event) => updateShot(activeShot.id, { composition: event.target.value })} className="w-full rounded-lg border border-white/10 bg-[#111318] px-3 py-2 text-xs text-gray-300 outline-none focus:border-blue-500/60" /></Field>
                      <Field label="道具与环境"><input value={activeShot.environment || ''} onChange={(event) => updateShot(activeShot.id, { environment: event.target.value })} className="w-full rounded-lg border border-white/10 bg-[#111318] px-3 py-2 text-xs text-gray-300 outline-none focus:border-blue-500/60" /></Field>
                    </div>
                  </div>
                  <div className="space-y-4 rounded-2xl border border-white/10 bg-[#101217] p-4">
                    <div className="flex items-center gap-2 text-xs font-semibold text-white"><Clapperboard className="h-4 w-4 text-violet-400" /> 专业镜头库</div>
                    <Field label="镜头景别"><SelectField value={activeShot.shotSize || activeShot.title} options={shotSizes} onChange={(value) => updateShot(activeShot.id, { shotSize: value, title: value })} /></Field>
                    <Field label="运镜方式"><SelectField value={activeShot.cameraMove} options={cameraMoves} onChange={(value) => updateShot(activeShot.id, { cameraMove: value })} /></Field>
                    <Field label="出镜角色"><input value={activeShot.character || ''} onChange={(event) => updateShot(activeShot.id, { character: event.target.value })} className="w-full rounded-lg border border-white/10 bg-[#111318] px-3 py-2 text-xs text-gray-300 outline-none" /></Field>
                    <Field label="镜头时长"><div className="flex items-center gap-2"><input type="range" min="1" max="8" value={activeShot.duration || 3} onChange={(event) => updateShot(activeShot.id, { duration: Number(event.target.value) })} className="min-w-0 flex-1 accent-blue-500" /><span className="w-8 text-right text-xs text-gray-400">{activeShot.duration || 3}s</span></div></Field>
                    <button onClick={() => addShot(shots.indexOf(activeShot) + 1)} className="mt-2 flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-white/15 py-2.5 text-xs text-gray-400 hover:border-blue-500/40 hover:text-blue-400"><Plus className="h-3.5 w-3.5" /> 在下方插入镜头</button>
                  </div>
                </div>
              </main>
            )}
          </div>
        )}
      </div>

      {isGenerating && (
        <GenerationOverlay
          title="正在批量渲染分镜图"
          subtitle={`创世 First 正在为 ${shots.length} 个镜头统一人物、场景和电影视觉风格。`}
          duration={demoDuration(isDemo ? 12000 : 900)}
          steps={['锁定角色与视觉风格', '并行渲染高清分镜', '检查镜头连续性', '整理分镜资产']}
          onComplete={isDemo ? finishDemoGeneration : finishRealGeneration}
        />
      )}
    </div>
  );
}

export default StoryboardResultPage;
